const SearchState = {
  isSearchModalOpen: false,
  suppressOpenSearch: false
};

class ComponentPredictiveSearchLink extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleSearchModal(event);
    });
  }

  toggleSearchModal(event) {
    event.preventDefault();

    if (SearchState.suppressOpenSearch) {
      return;
    }

    const searchModal = document.querySelector("component-predictive-search");

    if (window.innerWidth <= 1024) {
      const sideMenu = document.getElementById('side-menu');
      const bodyCover = document.getElementById('body-cover');
      const navToggle = document.getElementById('nav-toggle');

      if (sideMenu && sideMenu.classList.contains('open')) {
        sideMenu.classList.remove('open');
        if (bodyCover) bodyCover.style.display = 'none';
        if (navToggle) navToggle.classList.remove('open');
        window.SideMenuOpen = false;
      }
    }

    if (SearchState.isSearchModalOpen) {
      return;
    }

    searchModal.style.display = "block";
    searchModal.setActive();

    const searchInput = searchModal.querySelector('input[name="q"]');

    if (searchInput) {
      searchInput.focus();
    }

    SearchState.isSearchModalOpen = true;
  }
}

customElements.define("component-predictive-search-link", ComponentPredictiveSearchLink);

class ComponentPredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.cachedResults = {};
    this.isOpen = false;
  }

  setActive() {
    this.searchModal = this.closest('.search-modal');
    this.input = this.querySelector('input[name="q"]');
    this.clearButton = this.querySelector('.search-modal__close');
    this.resultsContainer = this.querySelector('.predictive-search__results');
    this.recommendations = this.querySelector('#predictive-search-recommendations');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.loadingState = this.querySelector('.search-loading');

    this.applyMinHeights();
    this.setupEventListeners();
  }

  applyMinHeights() {
    if (!this.searchModal) {
      return;
    }

    const modalHeight = this.searchModal.getBoundingClientRect().height;

    if (modalHeight > 0) {
      this.searchModal.style.minHeight = `${modalHeight}px`;
    }

    if (this.resultsContainer) {
      const resultsHeight = this.resultsContainer.getBoundingClientRect().height;

      if (resultsHeight > 0) {
        this.resultsContainer.style.minHeight = `${resultsHeight}px`;
      }
    }
  }

  setupEventListeners() {
    if (this.eventListenersAttached) {
      return;
    }

    const form = this.querySelector('form.search');

    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('input', this.debounce((event) => {
        this.onChange(event);
      }, 300));

      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.clearSearchField.bind(this));
    }

    if (!this.documentClickListenerAdded) {
      document.addEventListener('mousedown', this.handleDocumentClick.bind(this));
      this.documentClickListenerAdded = true;
    }

    this.eventListenersAttached = true;
  }

  handleDocumentClick(event) {
    if (!SearchState.isSearchModalOpen) return;

    const isClickInside = this.contains(event.target);

    if (!isClickInside) {
      SearchState.suppressOpenSearch = true;

      setTimeout(() => {
        SearchState.suppressOpenSearch = false;
      }, 100);

      this.close();
    }
  }

  debounce(fn, wait) {
    let timeout;

    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  getQuery() {
    const query = this.input ? this.input.value.trim() : '';
    return query;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.input.classList.remove('has-content');

      if (this.clearButton) {
        this.clearButton.classList.remove('has-content');
      }

      if (document.activeElement !== this.input) {
        this.close(true);
      }

      return;
    }

    this.input.classList.add('has-content');

    if (this.clearButton) {
      this.clearButton.classList.add('has-content');
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
    }
  }

  onFocus() {
    const searchTerm = this.getQuery();

    this.input.classList.add('has-content');

    if (!searchTerm.length) {
      return;
    }

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) {
      return;
    }

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) {
      return;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) {
      return;
    }

    activeElement.setAttribute('aria-selected', true);

    if (selectedElement) {
      selectedElement.setAttribute('aria-selected', false);
    }

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.loadingState) {
      this.loadingState.style.display = 'block';
      this.loadingState.style.opacity = '1';
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,page&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,vendor,tag,body&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        this.close();
      })
      .finally(() => {
        if (this.loadingState) {
          this.loadingState.style.display = 'none';
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    if (this.searchModal) {
      requestAnimationFrame(() => {
        this.searchModal.style.minHeight = '';
      });
    }

    if (this.resultsContainer) {
      requestAnimationFrame(() => {
        this.resultsContainer.style.minHeight = '';
      });
    }

    this.setLiveRegionResults();
    this.open();
  }

  clearSearchField() {
    if (this.input) {
      this.input.value = '';
      this.input.classList.remove('has-content');
    }
    if (this.clearButton) {
      this.clearButton.classList.remove('has-content');
    }
    this.close(true);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('theme-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {

    if (!SearchState.isSearchModalOpen) {
      return;
    }

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.style.display = 'none';

    this.isOpen = false;
    SearchState.isSearchModalOpen = false;


    if (this.recommendations) {
      this.recommendations.classList.remove('visually-hidden');
    }
  }
}

customElements.define('component-predictive-search', ComponentPredictiveSearch);
